import React, { useRef, useEffect, useCallback } from 'react';
import { trackClick } from '../utils/telemetry';
import './AcquisitionBanner.css';

const FRICTION = 0.96;
const PARTICLE_COUNT = 70;
const FLAKE_COUNT = 140;
const SNOW_DURATION = 5000;

const AcquisitionBanner = () => {
  const cardRef = useRef(null);
  const canvasRef = useRef(null);
  const rotX = useRef(0);
  const rotY = useRef(0);
  const vX = useRef(0);
  const vY = useRef(0);
  const isDragging = useRef(false);
  const clickCount = useRef(0);
  const floatPhase = useRef(0);
  const floatRAF = useRef(null);
  const explodeRAF = useRef(null);
  const audioCtx = useRef(null);

  const getAudioCtx = () => {
    if (!audioCtx.current) {
      audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtx.current;
  };

  const playShakeSound = () => {
    try {
      const ctx = getAudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(80, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(20, ctx.currentTime + 0.3);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch {}
  };

  const playExplosionSound = () => {
    try {
      const ctx = getAudioCtx();
      const bufferSize = ctx.sampleRate * 0.4;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.05));
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(1000, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.4);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      noise.connect(filter).connect(gain).connect(ctx.destination);
      noise.start(ctx.currentTime);
      noise.stop(ctx.currentTime + 0.4);
    } catch {}
  };

  const playWindSound = () => {
    try {
      const ctx = getAudioCtx();
      const bufferSize = ctx.sampleRate * 2;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 1.5));
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(400, ctx.currentTime);
      filter.Q.setValueAtTime(0.5, ctx.currentTime);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 2);
      noise.connect(filter).connect(gain).connect(ctx.destination);
      noise.start(ctx.currentTime);
      noise.stop(ctx.currentTime + 2);
    } catch {}
  };

  const explodeParticles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2;
    const cy = H / 2;

    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: cx,
      y: cy,
      vx: (Math.random() - 0.5) * 20,
      vy: (Math.random() - 0.5) * 20 - 5,
      size: Math.random() * 8 + 3,
      hue: 350 + Math.random() * 15,
      life: 1,
      decay: 0.008 + Math.random() * 0.012,
    }));

    const flakes = Array.from({ length: FLAKE_COUNT }, () => ({
      x: Math.random() * W,
      y: Math.random() * H * -1,
      vx: (Math.random() - 0.5) * 1.5,
      vy: 0.5 + Math.random() * 1.5,
      size: Math.random() * 4 + 1,
      opacity: 0.3 + Math.random() * 0.7,
    }));

    const startTime = Date.now();

    const animate = () => {
      ctx.clearRect(0, 0, W, H);
      let alive = false;

      for (const p of particles) {
        if (p.life <= 0) continue;
        alive = true;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.15;
        p.vx *= 0.99;
        p.vy *= 0.99;
        p.life -= p.decay;
        ctx.globalAlpha = p.life;
        ctx.fillStyle = `hsl(${p.hue}, 80%, 60%)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;

      const elapsed = Date.now() - startTime;
      if (elapsed < SNOW_DURATION) {
        for (const f of flakes) {
          f.x += f.vx + Math.sin(elapsed * 0.001 + f.y * 0.01) * 0.3;
          f.y += f.vy;
          if (f.y > H + 10) {
            f.y = -10;
            f.x = Math.random() * W;
          }
          ctx.globalAlpha = f.opacity * Math.min(1, (SNOW_DURATION - elapsed) / 1000);
          ctx.fillStyle = '#c41e3a';
          ctx.beginPath();
          ctx.arc(f.x, f.y, f.size, 0, Math.PI * 2);
          ctx.fill();
        }
        explodeRAF.current = requestAnimationFrame(animate);
      } else if (alive) {
        explodeRAF.current = requestAnimationFrame(animate);
      }

      ctx.globalAlpha = 1;
    };

    animate();
  }, []);

  const handlePointerDown = (e) => {
    isDragging.current = true;
    const el = cardRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current) return;
    vX.current += e.movementY * 0.3;
    vY.current -= e.movementX * 0.3;
  };

  const handlePointerUp = (e) => {
    isDragging.current = false;
    const el = cardRef.current;
    if (el) el.releasePointerCapture(e.pointerId);
  };

  const handleClick = () => {
    clickCount.current += 1;
    if (clickCount.current === 1) {
      playShakeSound();
      trackClick('/acquisition-banner');
      window.open('https://hegxib.me/contact', '_blank');
    } else if (clickCount.current === 2) {
      playExplosionSound();
      playWindSound();
      const card = cardRef.current;
      if (card) {
        card.classList.add('acq-banner-shake');
        setTimeout(() => {
          card.classList.remove('acq-banner-shake');
          card.classList.add('acq-banner-exploded');
          if (canvasRef.current) {
            canvasRef.current.width = card.offsetWidth;
            canvasRef.current.height = card.offsetHeight;
          }
          explodeParticles();
          if (floatRAF.current) cancelAnimationFrame(floatRAF.current);
        }, 400);
      }
    }
  };

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const floatLoop = () => {
      floatPhase.current += 0.02;
      if (!card.classList.contains('acq-banner-exploded')) {
        card.style.transform = `translateY(${Math.sin(floatPhase.current) * 6}px)`;
      }
      floatRAF.current = requestAnimationFrame(floatLoop);
    };
    floatRAF.current = requestAnimationFrame(floatLoop);

    return () => {
      if (floatRAF.current) cancelAnimationFrame(floatRAF.current);
      if (explodeRAF.current) cancelAnimationFrame(explodeRAF.current);
      if (audioCtx.current) audioCtx.current.close();
    };
  }, []);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    let rotFrame;

    const rotateLoop = () => {
      rotX.current *= FRICTION;
      rotY.current *= FRICTION;
      if (!card.classList.contains('acq-banner-exploded')) {
        card.style.setProperty('--rot-x', `${rotX.current}deg`);
        card.style.setProperty('--rot-y', `${rotY.current}deg`);
      }
      rotFrame = requestAnimationFrame(rotateLoop);
    };
    rotFrame = requestAnimationFrame(rotateLoop);

    return () => cancelAnimationFrame(rotFrame);
  }, []);

  return (
    <div
      className="acq-banner"
      ref={cardRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
    >
      <div className="acq-banner-shine" />
      <div className="acq-banner-glow" />
      <div className="acq-banner-radial" />
      <div className="acq-banner-content">
        <span className="acq-banner-logo">東京</span>
        <p className="acq-banner-text">WEAR</p>
        <p className="acq-banner-sub">فرصة استثمارية</p>
        <span className="acq-banner-cta">← تواصل مع المطور</span>
      </div>
      <canvas ref={canvasRef} className="acq-banner-canvas" />
    </div>
  );
};

export default AcquisitionBanner;
